import { useContext, useEffect, useState } from "react";
import {EncryptedPubKey, PaymentInfoContext} from "../App";
import { errorHandler } from "../utils/errorHandler";
import useDebounce from "./useDebounce";
import {checkoutComEvents} from "../events/checkoutCom.events";
import JSEncrypt from "jsencrypt";
import {baseEvyPayUrl, ENCRYPTION_PUB_KEY} from "../constants";
import axios from "../api/axios";
import {IolpayEvents} from "../events/iolpay.events";
import {PaymentAccountPubKeyContext} from "../containers/InitCheckoutContainer";
import {transformToYYYY} from "../utils";


const usePaymentMethod = (formik, setLoading, setErrorString, walletItem, currentPaymentSystem) => {
  const paymentInfo = useContext(PaymentInfoContext);
  const [paymentMethod, setPaymentMethod] = useState({paymentMethod: null, error: null});
  const postalCode = useDebounce(formik.values.zip, 500);
  const holderName = useDebounce(formik.values.cardName, 500);
  const encryptedPubKey = useContext(EncryptedPubKey);
  const { paymentAccountPubKey } = useContext(PaymentAccountPubKeyContext)



  const tokenizeEvyPayCard = async (encryptedCardInfo) => {
    if(!encryptedCardInfo) return;

    try {
      const {data} = await axios.post(`/tokens`, {
        payload: encryptedCardInfo,
      }, {
        baseURL: baseEvyPayUrl,
        headers: {
          Authorization: `Bearer ${paymentAccountPubKey}`,
        }
      });

      return data
    } catch (e) {
      return e.response.data
    }
  }

  useEffect(() => {
    const unsubscribeCheckoutTokenized = checkoutComEvents.on("cardTokenized", (e) => {
      setLoading(false);
      const res = mapCheckoutPaymentMethod(e);
      setPaymentMethod({
        paymentMethod: res,
        error: null,
      });
    });

    const unsubscribeIOLPayTokenized = IolpayEvents.on("cardTokenized", (e) => {
      setLoading(false);
      const res = mapEvyPayPaymentMethod(e);
      setPaymentMethod({
        paymentMethod: res,
        error: null,
      });
    });

    return () => {
      unsubscribeCheckoutTokenized();
      unsubscribeIOLPayTokenized()
    }
  }, []);

  useEffect(() => {
    setErrorString('')
    if (
      (formik?.values?.cardNumber?.complete &&
      formik?.values?.cardExpiry?.complete &&
      formik?.values?.cardCvc?.complete) && !walletItem
    ) {
      (async () => {
        setTimeout(async () => {
          switch (currentPaymentSystem.code) {
            case 'checkout': {
              window.Frames?.enableSubmitForm()
              setLoading(true);
              if (postalCode) {
                window.Frames.cardholder = {
                  ...(holderName && {
                    name: holderName,
                  }),
                };
              }
              window.Frames?.submitCard();
              break;
            }
            case 'evypay': {
              const isNameValid = holderName && holderName.length > 0;
              // const isPostalCodeValid = postalCode && postalCode.length > 0;

              if(!isNameValid)
                return
              // setLoading(true);
              const crypt = new JSEncrypt();
              const pubkey = atob(ENCRYPTION_PUB_KEY);

              crypt.setPublicKey(pubkey);

              const object = {
                "cardNumber": formik.values.cardNumber.value.replace(/\s/g, ''),
                "expiryMonth": formik.values.cardExpiry.value.split('/')[0],
                "expiryYear": transformToYYYY(formik.values.cardExpiry.value.split('/')[1]),
                "cvv": formik.values.cardCvc.value,
                "cardHolder": holderName,
                "postalCode": "postal",
                "country": "RU",
                type: "card"
              }

              const input = JSON.stringify(object);
              const encryptedCardInfo = crypt.encrypt(input);

              const res = await tokenizeEvyPayCard(encryptedCardInfo);

              if (res?.token) {
                IolpayEvents.emit("cardTokenized", res)
              } else {
                setLoading(false);
                res.errors.map(({ field, message }) => {
                  const element = {
                    CardNumber: 'cardNumber',
                    ExpiryMonth: 'cardExpiry',
                    ExpiryYear: 'cardExpiry',
                    CardExpiry: 'cardExpiry',
                    Cvv: 'cardCvc',
                  }
                  if(element[field]) {
                    formik.setFieldValue(element[field], {
                      ...formik.values[element[field]],
                      complete: false,
                      error: {
                        message,
                      }
                    });
                    formik.setFieldError(element[field] , message);
                  }
                })
                errorHandler(res);
              }

              break;
            }
          }
        }, 1000)
      })()
    } else if (walletItem) {
      const paymentMethod = mapWalletItem(walletItem);
      setPaymentMethod({ paymentMethod, error: null });
    }
    else {
      setPaymentMethod({ paymentMethod: null, error: null });
    }
  }, [formik.values.cardNumber, formik.values.cardExpiry, formik.values.cardCvc, postalCode, holderName, walletItem])

  const mapEvyPayPaymentMethod = (paymentMethod) => {
    return {
      id: paymentMethod.token,
      object: "payment_method",
      card: {
        brand: paymentMethod.brand,
        brand_product: paymentMethod.productType,
        country: paymentMethod.issuerCountry,
        exp_month: paymentMethod.expiryMonth,
        exp_year: paymentMethod.expiryYear,
        last4: paymentMethod.last4,
      },
      type: paymentMethod.cardType,
    }
  }
  const mapCheckoutPaymentMethod = (paymentMethod) => {
    return {
      id: paymentMethod.token,
      object: "payment_method",
      card: {
        brand: paymentMethod.scheme,
        brand_product: paymentMethod.product_type,
        country: paymentMethod.issuer_country,
        exp_month: paymentMethod.expiry_month,
        exp_year: paymentMethod.expiry_year,
        // funding: paymentMethod.funding,
        last4: paymentMethod.last4,
      },
      type: paymentMethod.type,
    }
  }

  const mapWalletItem = (walletItem) => {
    if (!walletItem) return null;
    const paymentMethod = walletItem.paymentMethod;
    return {
      id: paymentMethod.externalId,
      object: "payment_method",
      card: {
        brand: paymentMethod.card.brand,
        brand_product: paymentMethod.card.brandProduct,
        country: paymentMethod.card.country,
        exp_month: paymentMethod.card.expMonth,
        exp_year: paymentMethod.card.expYear,
        funding: paymentMethod.card.funding,
        last4: paymentMethod.card.last4,
      },
      type: paymentMethod.type,
    }
  };

  return paymentMethod;
}

export default usePaymentMethod
